/**
 * useKeywordVariant — A/B test hook for SEO-driven headline variants.
 *
 * Tests different hero titles/subtitles based on top-performing keywords
 * from keyword research. Persists variant assignment in localStorage for
 * consistent UX across sessions. Fires GA4 event on first assignment.
 *
 * Usage:
 *   const { headline, subheadline, variant } = useKeywordVariant();
 *   // Then render headline in your hero section
 *
 * GA4 Integration:
 *   - Event: `keyword_variant_assigned` (fired once per user)
 *   - Dimension: `seo_headline_variant` (attach to quiz_started, buy_now_clicked)
 */

import { useState } from 'react';

const STORAGE_KEY = 'pickify_seo_headline_variant';

/**
 * Variant definitions.
 *
 * "control" = current copy (no change)
 * "variant_a" = top trend keyword: "AI Shopping Assistant" (trend: 36)
 * "variant_b" = rising query: emotional + urgency framing
 *
 * Update these when you re-run seo-optimizer with fresh keyword data.
 *
 * NOTE: All variants use "AI Gift Finder" as the H1 headline to avoid
 * confusing Googlebot with different H1s on each crawl. Only subheadline,
 * badge, and CTA are A/B tested.
 */
const VARIANTS = {
  control: {
    headline: 'AI Gift Finder',
    subheadline: 'Free Personalized Gift Ideas in 30 Seconds',
    badge: 'Free • Fast • Personalized',
    cta: 'Start Quiz',
  },
  variant_a: {
    headline: 'AI Gift Finder',
    subheadline: 'Unlock Personalized Gift Ideas in 30 Seconds',
    badge: 'Free • AI-Powered • Instant',
    cta: 'Find My Gift',
  },
  variant_b: {
    headline: 'AI Gift Finder',
    subheadline: "The Perfect Gift — Found in 30 Seconds",
    badge: 'Free • No Signup • Instant',
    cta: 'Surprise Them',
  },
};

const VARIANT_KEYS = Object.keys(VARIANTS);
const WEIGHTS = [0.34, 0.33, 0.33]; // roughly even split

function assignVariant() {
  const roll = Math.random();
  let cumulative = 0;
  for (let i = 0; i < VARIANT_KEYS.length; i++) {
    cumulative += WEIGHTS[i];
    if (roll < cumulative) return VARIANT_KEYS[i];
  }
  return VARIANT_KEYS[VARIANT_KEYS.length - 1];
}

export function useKeywordVariant() {
  const [variant] = useState(() => {
    // Check for existing assignment
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && VARIANTS[stored]) return stored;
    } catch {
      // localStorage unavailable
    }

    // Assign new variant
    const assigned = assignVariant();

    try {
      localStorage.setItem(STORAGE_KEY, assigned);
    } catch {
      // Silent fail
    }

    return assigned;
  });

  return {
    ...VARIANTS[variant],
    variant,
    // Utility: get variant name to attach to other GA4 events
    variantName: variant,
  };
}

/**
 * Get the current keyword variant without React (for analytics.js).
 * Returns the variant key string, or 'control' if not yet assigned.
 */
export function getKeywordVariant() {
  try {
    return localStorage.getItem(STORAGE_KEY) || 'control';
  } catch {
    return 'control';
  }
}
